import type { PluginSidebarProps } from "@d12labs/plugins/types";
import { BrandSquare, SidebarBrand, SidebarNavItem, SidebarSection } from "@d12labs/ui/sidebar-kit";
import { useQuery } from "@tanstack/react-query";
import {
	CalendarClock,
	Dumbbell,
	Flame,
	LayoutDashboard,
	ListPlus,
	ScrollText,
	StretchHorizontal,
} from "lucide-react";
import { fitstreakApi } from "./api";

/**
 * Contextual sidebar for FitStreak: dashboard + the three quick-log entry points
 * + the full log and goals. A live streak pill at the top keeps the number that
 * matters in view from every page.
 */
export const pluginSidebar = ({ onBack, onNavigate }: PluginSidebarProps) => {
	const dashboard = useQuery({
		queryKey: ["fitstreak", "dashboard"],
		queryFn: () => fitstreakApi.dashboard(),
	});
	const streak = dashboard.data?.streak;

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={Flame} />}
				title="FitStreak"
				subtitle="Keep the chain alive"
				onBack={onBack}
			/>
			<div className="flex flex-col gap-1 px-2 py-2">
				{streak ? (
					<div className="mx-1 mb-1 flex items-center gap-2 rounded-lg border border-hairline bg-surface-1 px-3 py-2">
						<Flame className="h-4 w-4 text-primary" />
						<span className="text-sm font-semibold tabular-nums">
							{streak.current} day{streak.current === 1 ? "" : "s"}
						</span>
						<span className="caption ml-auto text-ink-subtle">
							{streak.todayCounts ? "today ✓" : "today —"}
						</span>
					</div>
				) : null}

				<SidebarSection label="Overview">
					<SidebarNavItem
						to="/fitstreak"
						exact
						icon={LayoutDashboard}
						label="Dashboard"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/fitstreak/activities"
						icon={ScrollText}
						label="Activity log"
						onNavigate={onNavigate}
					/>
				</SidebarSection>

				<SidebarSection label="Log">
					<SidebarNavItem
						to="/fitstreak/log/strength"
						icon={Dumbbell}
						label="Strength"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/fitstreak/log/mobility"
						icon={StretchHorizontal}
						label="Mobility"
						onNavigate={onNavigate}
					/>
					<SidebarNavItem
						to="/fitstreak/log/activity"
						icon={ListPlus}
						label="Activity"
						onNavigate={onNavigate}
					/>
				</SidebarSection>

				<SidebarSection label="Plan">
					<SidebarNavItem
						to="/fitstreak/goals"
						icon={CalendarClock}
						label="Goals & races"
						onNavigate={onNavigate}
					/>
				</SidebarSection>
			</div>
		</div>
	);
};
